import { DateTime } from "luxon";
import { useOptionallyControlledState } from "../../../hooks/useOptionallyControlledState";
import { MaskedDateInputChange, MaskedDateInputRef } from "./MaskedDateInput";
import { RefObject } from "react";

export type DateFieldSingleStateChange = {
  date: DateTime | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
};

export type DateFieldSingleStateChangeHandler = (
  change: DateFieldSingleStateChange,
) => void;

export type DateFieldSingleStateParam = {
  /**
   * The controlled value of the date field.
   */
  valueProp?: DateTime | null;
  /**
   * The default (uncontrolled) value of the date field.
   */
  defaultValueProp?: DateTime | null;
  /**
   * The ref to the input field.
   */
  inputRef: RefObject<MaskedDateInputRef>;
  /**
   * Callback for when the date changes.
   */
  onChange: DateFieldSingleStateChangeHandler;
};

export type DateFieldSingleState = {
  /**
   * The current value (DateTime) of the date field.
   */
  value: DateTime | null;
  /**
   * Sets the value of the date field.
   */
  setValue: (value: DateTime | null) => void;
  /**
   * Handles the change of the input field.
   */
  handleInputChange: (change: MaskedDateInputChange) => void;
  /**
   * Handles the selection of a date in the calendar.
   */
  handleCalendarSelection: (date: DateTime) => void;
};

/**
 * This is a hook for keeping state in sync between a date input and calendar.
 */
export function useDateFieldSingleState({
  valueProp,
  defaultValueProp,
  inputRef,
  onChange,
}: DateFieldSingleStateParam): DateFieldSingleState {
  const [value, setValue] = useOptionallyControlledState<DateTime | null>({
    controlledValue: valueProp,
    defaultValue: defaultValueProp,
  });

  const setSharedValue = (date: DateTime | null) => {
    inputRef.current?.setDate(date);
    setValue(date);
  };

  const handleInputChange = (change: MaskedDateInputChange) => {
    const date = change.isInputEmpty
      ? null
      : (change.date?.startOf("day") ?? null);
    onChange?.({
      date,
      isInputValid: change.isInputValid,
      isInputEmpty: change.isInputEmpty,
    });
    if (change.isInputValid) {
      setSharedValue(change.isInputEmpty ? null : change.date);
    }
    if (change.isInputEmpty) {
      setSharedValue(null);
      return;
    }
    if (change.date) {
      setSharedValue(change.date);
    }
  };

  const handleCalendarSelection = (date: DateTime) => {
    setSharedValue(date);
    onChange?.({
      date,
      isInputValid: true,
      isInputEmpty: false,
    });
  };

  return {
    value,
    setValue: setSharedValue,
    handleInputChange,
    handleCalendarSelection,
  };
}
